"use client"

import type { ReactNode } from "react"
import OverviewStats from "./overview-stats"
import ContentChart from "./content-chart"
import RecentPosts from "./recent-posts"
import PopularArticles from "./popular-articles"
import ContentCategories from "./content-categories"
import UserActivity from "./user-activity"
import SystemHistory from "./system-history"
import LiveTicker from "./live-ticker"
import DashboardHeader from "./dashboard-header"

function SectionDivider({ eyebrow, title, hint }: { eyebrow: string; title: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/40 pb-2.5">
      <div className="flex items-baseline gap-3">
        <span className="font-display text-base italic text-[var(--brand-amber)] tabular-nums">{eyebrow}</span>
        <span className="text-border">/</span>
        <h2 className="text-sm font-medium text-foreground">{title}</h2>
      </div>
      {hint && <span className="font-mono text-[11px] text-muted-foreground">{hint}</span>}
    </div>
  )
}

function Stagger({ delay = 0, className = "", children }: { delay?: number; className?: string; children: ReactNode }) {
  return (
    <div
      className={`animate-stagger-in ${className}`}
      style={{ ["--stagger-delay" as string]: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

export default function CMSDashboardContent() {
  // Ordem do DOM = ordem mobile: a operadora abre o painel no celular pra
  // copiar o último cupom e conferir se o bot vive — isso vem logo após o
  // header. No desktop (lg:order-*) o hero editorial volta pro topo.
  return (
    <div className="flex w-full min-w-0 flex-col gap-12 sm:gap-14">
      <div className="lg:order-1">
        <DashboardHeader />
      </div>

      {/* Live operation — primeiro no mobile: cupons + robôs */}
      <section className="space-y-5 lg:order-4">
        <SectionDivider eyebrow="01" title="Operação ao vivo" hint="updates contínuos" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
          <Stagger delay={220} className="h-full lg:order-2">
            <UserActivity />
          </Stagger>
          <Stagger delay={290} className="h-full lg:order-1">
            <SystemHistory />
          </Stagger>
        </div>
      </section>

      {/* Live ticker */}
      <Stagger delay={80} className="lg:order-2">
        <LiveTicker />
      </Stagger>

      {/* Hero KPIs */}
      <Stagger delay={140} className="lg:order-3">
        <OverviewStats />
      </Stagger>

      {/* Analysis */}
      <section className="space-y-5 lg:order-5">
        <SectionDivider eyebrow="02" title="Análise de captura" hint="últimos 14 dias" />
        <Stagger delay={360}>
          <ContentChart />
        </Stagger>
      </section>

      {/* Discoveries */}
      <section className="space-y-5 lg:order-6">
        <SectionDivider eyebrow="03" title="Descobertas recentes" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:gap-6">
          <Stagger delay={500} className="lg:col-span-2">
            <RecentPosts />
          </Stagger>
          <Stagger delay={570}>
            <ContentCategories />
          </Stagger>
        </div>
        <Stagger delay={640}>
          <PopularArticles />
        </Stagger>
      </section>
    </div>
  )
}
